import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Shield, TrendingUp, BarChart3, Users, Coins, Search, Gift, X, AlertTriangle, DollarSign, Brain, Loader2, RefreshCw } from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminPanel = ({ 
  open, 
  onOpenChange, 
  adminDashboard,
  adminUsers,
  onSearchUsers,
  onGiftCredits
}) => {
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [giftAmount, setGiftAmount] = useState(100);
  const [costs, setCosts] = useState(null);
  const [selfLearn, setSelfLearn] = useState(null);
  const [learning, setLearning] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem("kaan_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchSelfLearn = async () => {
    try {
      const res = await axios.get(`${API}/admin/self-learn/status`, { headers: authHeaders() });
      setSelfLearn(res.data);
    } catch (e) {
      console.warn("self-learn status failed", e);
    }
  };

  const runSelfLearn = async () => {
    setLearning(true);
    try {
      await axios.post(`${API}/admin/self-learn/run`, {}, { headers: authHeaders() });
      toast.success("Öğrenme başlatıldı — internetten araştırıyor. Birkaç dakika sürebilir.");
      // Refresh status a few times as the background job progresses
      setTimeout(fetchSelfLearn, 8000);
      setTimeout(fetchSelfLearn, 25000);
      setTimeout(() => { fetchSelfLearn(); setLearning(false); }, 45000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Öğrenme başlatılamadı");
      setLearning(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("kaan_token");
        const res = await axios.get(`${API}/admin/costs`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) setCosts(res.data);
      } catch (e) {
        console.warn("costs fetch failed", e);
      }
      if (!cancelled) fetchSelfLearn();
    })();
    return () => { cancelled = true; };
  }, [open]);

  const handleGiftCredits = async (userId) => {
    await onGiftCredits(userId, giftAmount);
    setSelectedUserId(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Shield size={20} /> {t('admin_panel')}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Monthly Cost & Margin Alert (new) */}
          {costs && (
            <div
              data-testid="admin-costs-card"
              className={`rounded-lg p-4 border ${
                costs.margin_alert
                  ? "bg-red-500/10 border-red-500/40"
                  : "bg-zinc-800/60 border-zinc-700"
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <DollarSign size={18} className={costs.margin_alert ? "text-red-400" : "text-emerald-400"} />
                  <span className="text-sm font-semibold text-white">
                    {costs.month} · {t('cost_dashboard') || 'Aylık Maliyet & Marj'}
                  </span>
                </div>
                {costs.margin_alert && (
                  <span
                    data-testid="admin-cost-alert"
                    className="inline-flex items-center gap-1 text-xs font-bold text-red-300 bg-red-500/20 border border-red-500/40 px-2 py-1 rounded-full"
                  >
                    <AlertTriangle size={12} /> {t('margin_alert') || 'Marj Düşük'}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500">Gelir</div>
                  <div className="text-lg font-bold text-emerald-300" data-testid="admin-cost-revenue">
                    ₺{(costs.revenue_try || 0).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500">Maliyet</div>
                  <div className="text-lg font-bold text-orange-300" data-testid="admin-cost-cost">
                    ₺{(costs.cost_try || 0).toLocaleString()} <span className="text-[10px] text-zinc-500">(${costs.cost_usd})</span>
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500">Marj</div>
                  <div className={`text-lg font-bold ${
                    costs.margin_pct === null ? "text-zinc-500" :
                    costs.margin_alert ? "text-red-300" : "text-emerald-300"
                  }`} data-testid="admin-cost-margin">
                    {costs.margin_pct !== null ? `${costs.margin_pct}%` : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-zinc-500">Kullanım</div>
                  <div className="text-xs text-zinc-300 leading-relaxed">
                    <div>📩 {costs.usage.msg} mesaj</div>
                    <div>🔊 {costs.usage.tts} TTS</div>
                    <div>🔬 {costs.usage.dr} DR</div>
                  </div>
                </div>
              </div>
              {costs.margin_alert && (
                <div className="mt-3 text-xs text-red-200/90 leading-relaxed">
                  ⚠️ Bu ay marj %{costs.alert_threshold_pct}&apos;in altına düştü. Ücretli plan fiyatlarını gözden geçir veya free tier kotalarını sıkılaştır.
                </div>
              )}
              <div className="mt-3 text-[10px] text-zinc-500">
                Rate: mesaj ${costs.rate_card_usd.msg} · TTS ${costs.rate_card_usd.tts} · DR ${costs.rate_card_usd.dr} · USD/TRY {costs.usd_try_fx}
              </div>
            </div>
          )}

          {/* Internet Self-Learning */}
          <div
            data-testid="admin-self-learn-card"
            className="rounded-lg p-4 border bg-indigo-500/10 border-indigo-500/40"
          >
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Brain size={18} className="text-indigo-300" />
                <span className="text-sm font-semibold text-white">İnternetten Kendi Kendine Öğrenme</span>
              </div>
              <Button
                onClick={runSelfLearn}
                disabled={learning}
                size="sm"
                className="bg-indigo-600 hover:bg-indigo-500 text-white h-8"
                data-testid="admin-self-learn-run-btn"
              >
                {learning ? <Loader2 size={14} className="animate-spin mr-1" /> : <RefreshCw size={14} className="mr-1" />}
                {learning ? "Öğreniyor..." : "Şimdi Öğren"}
              </Button>
            </div>
            <p className="text-xs text-indigo-200/80 mb-3 leading-relaxed">
              👎 alan cevapları internetten araştırıp düzeltir ve güncel konularda (kripto, döviz, teknoloji) bilgi tabanını yeniler. Her gece 03:00&apos;te otomatik çalışır.
            </p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-zinc-500">Öğrenilen Cevap</div>
                <div className="text-lg font-bold text-indigo-300" data-testid="admin-self-learn-learned">
                  {selfLearn?.learned_answers ?? "—"}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-zinc-500">Bilgi Konusu</div>
                <div className="text-lg font-bold text-indigo-300" data-testid="admin-self-learn-topics">
                  {selfLearn?.knowledge_topics ?? "—"}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-zinc-500">Son Çalışma</div>
                <div className="text-xs text-zinc-300" data-testid="admin-self-learn-lastrun">
                  {selfLearn?.last_run?.finished_at
                    ? new Date(selfLearn.last_run.finished_at).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })
                    : "Henüz yok"}
                </div>
              </div>
            </div>
            {selfLearn?.kb_entries?.length > 0 && (
              <div className="mt-3 space-y-1">
                {selfLearn.kb_entries.slice(0, 5).map((e, i) => (
                  <div key={i} className="text-[11px] text-zinc-400 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                    {e.topic}
                  </div>
                ))}
              </div>
            )}
          </div>


          {/* Dashboard Stats */}
          {adminDashboard && (
            <>
              {/* Revenue Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 border border-green-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-400 text-sm mb-1">
                    <TrendingUp size={16} /> Toplam Gelir
                  </div>
                  <div className="text-2xl font-bold text-white">₺{adminDashboard.revenue?.total?.toLocaleString() || 0}</div>
                </div>
                <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-blue-400 text-sm mb-1">
                    <BarChart3 size={16} /> Bugün
                  </div>
                  <div className="text-2xl font-bold text-white">₺{adminDashboard.revenue?.today?.toLocaleString() || 0}</div>
                </div>
                <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-purple-400 text-sm mb-1">
                    <Users size={16} /> Kullanıcılar
                  </div>
                  <div className="text-2xl font-bold text-white">{adminDashboard.users?.total || 0}</div>
                  <div className="text-xs text-zinc-400">{adminDashboard.users?.subscribed || 0} abonelik</div>
                </div>
                <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 border border-yellow-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-yellow-400 text-sm mb-1">
                    <Coins size={16} /> Sistemdeki Kredi
                  </div>
                  <div className="text-2xl font-bold text-white">{adminDashboard.activity?.total_credits_in_system?.toLocaleString() || 0}</div>
                </div>
              </div>

              {/* Subscription Stats */}
              <div className="bg-zinc-800 rounded-lg p-4">
                <div className="text-zinc-400 text-sm mb-3">Abonelik Dağılımı</div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-zinc-700/50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-400">{adminDashboard.subscriptions?.starter || 0}</div>
                    <div className="text-xs text-zinc-400">Starter</div>
                  </div>
                  <div className="text-center p-3 bg-zinc-700/50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-400">{adminDashboard.subscriptions?.pro || 0}</div>
                    <div className="text-xs text-zinc-400">Pro</div>
                  </div>
                  <div className="text-center p-3 bg-zinc-700/50 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-400">{adminDashboard.subscriptions?.premium || 0}</div>
                    <div className="text-xs text-zinc-400">Premium</div>
                  </div>
                </div>
              </div>

              {/* Activity Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-zinc-800 rounded-lg p-4">
                  <div className="text-zinc-400 text-sm mb-2">Aktivite</div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Toplam Sohbet</span>
                      <span className="text-white font-medium">{adminDashboard.activity?.total_conversations || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Toplam Mesaj</span>
                      <span className="text-white font-medium">{adminDashboard.activity?.total_messages || 0}</span>
                    </div>
                  </div>
                </div>
                <div className="bg-zinc-800 rounded-lg p-4">
                  <div className="text-zinc-400 text-sm mb-2">Bu Ay Gelir</div>
                  <div className="text-3xl font-bold text-green-400">₺{adminDashboard.revenue?.this_month?.toLocaleString() || 0}</div>
                </div>
              </div>
            </>
          )}

          {/* User Search and Management */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input 
                  placeholder={t('search')} 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && onSearchUsers(searchQuery)}
                  className="bg-zinc-800 border-zinc-700 pl-10"
                />
              </div>
              <Button onClick={() => onSearchUsers(searchQuery)} className="bg-blue-600 hover:bg-blue-700">
                <Search size={16} />
              </Button>
            </div>

            {/* Users Table */}
            <div className="bg-zinc-800 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-zinc-700">
                  <tr>
                    <th className="text-left p-3 text-zinc-300">{t('name')}</th>
                    <th className="text-left p-3 text-zinc-300">{t('email')}</th>
                    <th className="text-left p-3 text-zinc-300">{t('credits')}</th>
                    <th className="text-left p-3 text-zinc-300">{t('subscription')}</th>
                    <th className="text-left p-3 text-zinc-300">{t('edit')}</th>
                  </tr>
                </thead>
                <tbody>
                  {adminUsers.map((u) => (
                    <tr key={u.id} className="border-t border-zinc-700">
                      <td className="p-3 text-white">
                        {u.name}
                        {u.is_admin && <span className="ml-2 text-xs bg-purple-500/30 text-purple-300 px-2 py-0.5 rounded">{t('admin')}</span>}
                      </td>
                      <td className="p-3 text-zinc-400">{u.email}</td>
                      <td className="p-3 text-yellow-400 font-medium">{u.credits || 0}</td>
                      <td className="p-3">
                        {u.subscription?.status === "active" ? (
                          <span className="text-xs bg-green-500/30 text-green-300 px-2 py-0.5 rounded">{u.subscription.plan_name}</span>
                        ) : (
                          <span className="text-zinc-500">-</span>
                        )}
                      </td>
                      <td className="p-3">
                        {selectedUserId === u.id ? (
                          <div className="flex items-center gap-2">
                            <Input 
                              type="number" 
                              value={giftAmount}
                              onChange={(e) => setGiftAmount(parseInt(e.target.value) || 0)}
                              className="w-20 h-8 bg-zinc-700 border-zinc-600 text-sm"
                              placeholder={t('credits')}
                            />
                            <Button size="sm" onClick={() => handleGiftCredits(u.id)} className="bg-green-600 hover:bg-green-700 h-8">
                              <Gift size={14} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setSelectedUserId(null)} className="h-8">
                              <X size={14} />
                            </Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={() => setSelectedUserId(u.id)} className="text-yellow-400 hover:text-yellow-300">
                            <Gift size={14} className="mr-1" /> {t('credits')}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {adminUsers.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-4 text-center text-zinc-500">{t('no_results')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Transactions */}
          {adminDashboard?.recent_transactions?.length > 0 && (
            <div>
              <div className="text-zinc-400 text-sm mb-3">Son İşlemler</div>
              <div className="bg-zinc-800 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-700">
                    <tr>
                      <th className="text-left p-3 text-zinc-300">Tarih</th>
                      <th className="text-left p-3 text-zinc-300">E-posta</th>
                      <th className="text-left p-3 text-zinc-300">Paket</th>
                      <th className="text-left p-3 text-zinc-300">Tutar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminDashboard.recent_transactions.slice(0, 5).map((t, i) => (
                      <tr key={i} className="border-t border-zinc-700">
                        <td className="p-3 text-zinc-400">{new Date(t.created_at).toLocaleDateString('tr-TR')}</td>
                        <td className="p-3 text-white">{t.user_email}</td>
                        <td className="p-3 text-zinc-300">{t.package_id}</td>
                        <td className="p-3 text-green-400">₺{t.amount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
